import { TEntityRef, TProject } from '@ws/shared/types';

import * as acts from '../../utils/events/act.creators';
import { EventHolder } from '../events/EventHolder';
import { CHILD_MOVE_TYPE } from '../events/act.types';

/**
 * Detects cycles in the collection hierarchy
 *
 * @param project The project to check for cycles
 * @returns An array of collection IDs that are part of cycles
 */
function detectCycles(project: TProject): string[] {
  const parentMap = new Map<string, string | null>();

  // Create mapping of collection ID to parent ID
  Object.values(project.collections).forEach((collection) => {
    if (collection.id !== project.root && collection.id !== project.drafts) {
      parentMap.set(collection.id, collection.parentId);
    }
  });

  const cycleNodes: string[] = [];

  // For each collection, check if following parents leads to a cycle
  Object.keys(project.collections).forEach((collectionId) => {
    if (collectionId === project.root || collectionId === project.drafts) {
      return; // Skip root and drafts
    }

    const visited = new Set<string>();
    let currentId: string | null = collectionId;

    // Travel up the parent chain
    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);
      currentId = parentMap.get(currentId) || null;

      // If we've reached root or drafts, there's no cycle
      if (currentId === project.root || currentId === project.drafts) {
        currentId = null;
        break;
      }
    }

    // If currentId is not null, we found a cycle
    if (currentId) {
      // Mark all nodes in the cycle
      let cycleStart = currentId;
      let cycleNode = collectionId;

      // Add all nodes in the cycle
      while (cycleNode !== cycleStart) {
        if (!cycleNodes.includes(cycleNode)) {
          cycleNodes.push(cycleNode);
        }
        cycleNode = parentMap.get(cycleNode) || '';
      }

      // Add the start node
      if (!cycleNodes.includes(cycleStart)) {
        cycleNodes.push(cycleStart);
      }
    }
  });

  return cycleNodes;
}

/**
 * Rescues nodes by ensuring parent-child relationships are consistent
 * between parentId fields and children arrays. Prioritizes parentId.
 * Also detects and fixes circular references.
 *
 * @param project The project containing collections and notes
 * @returns An array of events to fix inconsistencies
 */
export function rescueNodes(project: TProject) {
  const eventHolder = new EventHolder();

  // Step 1: First pass - check all nodes and collect information
  const nodeParentMap = new Map<string, string | null>();
  const collectionChildrenMap = new Map<string, Set<string>>();

  // Initialize collection children sets
  Object.values(project.collections).forEach((collection) => {
    collectionChildrenMap.set(
      collection.id,
      new Set(collection.children.map((childRef) => childRef.id)),
    );
  });

  // Map all nodes by their parentId (source of truth)
  Object.values(project.collections).forEach((collection) => {
    if (collection.id !== project.root && collection.id !== project.drafts) {
      nodeParentMap.set(collection.id, collection.parentId);
    }
  });

  Object.values(project.notes).forEach((note) => {
    nodeParentMap.set(note.id, note.parentId);
  });

  // Step 1.5: Detect and fix circular references
  const cycleNodes = detectCycles(project);

  // Move all nodes in cycles to drafts collection
  cycleNodes.forEach((nodeId) => {
    const nodeRef: TEntityRef = { id: nodeId, type: 'collection' };

    // Update node's parent to drafts
    eventHolder.push(
      acts.updateNodeParent({
        ref: nodeRef,
        parentId: project.drafts,
      }),
    );

    // Find current parent of the node
    const currentParentId = project.collections[nodeId]?.parentId;
    if (currentParentId) {
      // Remove from old parent's children
      eventHolder.push(
        acts.updateCollectionChild({
          entityId: currentParentId,
          child: nodeRef,
          move: CHILD_MOVE_TYPE.REMOVE,
          position: 0,
        }),
      );
    }

    // Add to drafts collection
    eventHolder.push(
      acts.updateCollectionChild({
        entityId: project.drafts,
        child: nodeRef,
        move: CHILD_MOVE_TYPE.ADD,
        position: -1,
      }),
    );

    // Update the node parent in our map for subsequent steps
    nodeParentMap.set(nodeId, project.drafts);
  });

  // Step 2: Second pass - check for inconsistencies and create fixes

  // Check missing children in collections
  nodeParentMap.forEach((parentId, nodeId) => {
    if (parentId) {
      // Node claims this collection as parent
      const parentCollection = project.collections[parentId];

      if (!parentCollection) {
        // Parent collection doesn't exist, move to drafts
        const nodeType = project.notes[nodeId] ? 'note' : 'collection';
        const nodeRef: TEntityRef = { id: nodeId, type: nodeType };

        eventHolder.push(
          acts.updateNodeParent({
            ref: nodeRef,
            parentId: project.drafts,
          }),
        );

        eventHolder.push(
          acts.updateCollectionChild({
            entityId: project.drafts,
            child: nodeRef,
            move: CHILD_MOVE_TYPE.ADD,
            position: -1,
          }),
        );
      } else {
        // Parent exists but doesn't have this node in children
        const collectionChildren = collectionChildrenMap.get(parentId);

        if (collectionChildren && !collectionChildren.has(nodeId)) {
          const nodeType = project.notes[nodeId] ? 'note' : 'collection';
          const nodeRef: TEntityRef = { id: nodeId, type: nodeType };

          eventHolder.push(
            acts.updateCollectionChild({
              entityId: parentId,
              child: nodeRef,
              move: CHILD_MOVE_TYPE.ADD,
              position: -1,
            }),
          );
        }
      }
    }
  });

  // Check for children in collections that don't have corresponding parentId
  Object.values(project.collections).forEach((collection) => {
    collection.children.forEach((childRef) => {
      const node =
        childRef.type === 'note' ? project.notes[childRef.id] : project.collections[childRef.id];

      if (!node) {
        // Child reference points to non-existent node, remove it
        eventHolder.push(
          acts.updateCollectionChild({
            entityId: collection.id,
            child: childRef,
            move: CHILD_MOVE_TYPE.REMOVE,
            position: 0,
          }),
        );
      } else if (node.parentId !== collection.id) {
        // Child exists but has different parentId, remove from children
        eventHolder.push(
          acts.updateCollectionChild({
            entityId: collection.id,
            child: childRef,
            move: CHILD_MOVE_TYPE.REMOVE,
            position: 0,
          }),
        );
      }
    });
  });

  return eventHolder.getEvent();
}

/**
 * Repairs the project structure by ensuring parent-child relationships are consistent.
 * This function uses rescueNodes to generate the necessary events to fix inconsistencies,
 * but doesn't apply them directly. The caller is responsible for applying the events.
 *
 * @param project The project to repair
 * @returns The events needed to repair the project structure
 */
export function repairProjectStructure(project: TProject) {
  if (!project) {
    return null;
  }

  return rescueNodes(project);
}
